<!--
  AvatarZoomOverlay: a singleton "portrait peek" for the conversation.

  Clicking an in-prose avatar spawns *this* element (never mutating the original,
  which stays visible in place) and FLIP-animates from the clicked avatar's rect
  up to an enlarged square pinned at the top of the reading area. Because the
  clone is seeded exactly over the original, it reads as a copy peeling off and
  growing. This is a NON-modal peek, not a lightbox: there is no backdrop and
  nothing dims. The portrait is
  `pointer-events: none`, so the message list behind stays fully live — you can
  scroll and read underneath it while it floats.

  Dismissal favours closing (panic-click / edit safety): a capture-phase document
  click closes the peek and is swallowed, so a tap on the portrait, the message
  text, or a prose action button just closes and never fires that button. The
  only exceptions are the composer (`.send-msg`) and the app chrome
  (`.app-chrome`), which stay live and never dismiss. `Esc` also closes. Scroll
  and touch-drag pass through and never close.

  On close the portrait animates back into the source avatar's current rect; if
  the avatar has been scrolled out of the list's viewport, it fades out instead.
-->
<template>
  <div
    v-if="visible"
    ref="frameRef"
    class="portrait-zoom__frame"
    :style="frameStyle"
  >
    <BaseImage
      class="portrait-zoom__img"
      :src="src"
      :alt="alt"
      :image-focus="imageFocus"
      fallback-size="lg"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onBeforeUnmount, type CSSProperties } from 'vue'
import BaseImage from '@/components/ui/BaseImage.vue'
import type { ImageFocus } from '@/lib/media/imageAssetFields'

const props = withDefaults(defineProps<{
  open?: boolean
  src?: string | null
  alt?: string
  imageFocus?: ImageFocus | null
  /** The clicked avatar element — the FLIP source rect and what we hide/restore. */
  sourceEl?: HTMLElement | null
}>(), {
  open: false,
  src: null,
  alt: '',
  imageFocus: null,
  sourceEl: null,
})

const emit = defineEmits<{
  (event: 'close'): void  // user asked to dismiss (parent flips `open` to false)
  (event: 'closed'): void // out-animation finished; parent may clear its data
}>()

const DURATION = 260

// Selectors that stay live and never dismiss the peek. `.app-chrome` covers the
// header AND its teleported tools (avatar peek, menu) since #app-chrome-tools
// lives inside it; `.send-msg` is the composer band.
const KEEP_ALIVE_SELECTOR = '.send-msg, .app-chrome'

const visible = ref(false) // controls mount (v-if): true from open-start until fully closed
const frameRef = ref<HTMLElement | null>(null)
const frameStyle = ref<CSSProperties>({})

let closeTimer: ReturnType<typeof setTimeout> | null = null

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Wait two frames so a seeded (pre-transition) transform is committed before we
// release it — the classic FLIP double-rAF.
const nextFrame = (): Promise<void> =>
  new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())))

// Transform that maps the resting frame onto the source avatar's rect.
const flipTransform = (frame: HTMLElement): CSSProperties | null => {
  const source = props.sourceEl?.getBoundingClientRect()
  if (!source) return null
  const target = frame.getBoundingClientRect()
  if (target.width === 0 || target.height === 0) return null
  const scale = source.width / target.width
  const dx = source.left - target.left
  const dy = source.top - target.top
  return {
    transformOrigin: 'top left',
    transform: `translate(${dx}px, ${dy}px) scale(${scale})`,
  }
}

// Is the source avatar still within the message list's scroll viewport? If it
// has scrolled out, flying the portrait back to it looks wrong, so we fade.
const sourceInViewport = (): boolean => {
  const el = props.sourceEl
  if (!el) return false
  const source = el.getBoundingClientRect()
  const container = el.closest('.message-list-container')
  if (!container) return source.bottom > 0 && source.top < window.innerHeight
  const bounds = container.getBoundingClientRect()
  return source.bottom > bounds.top && source.top < bounds.bottom
}

const openZoom = async (): Promise<void> => {
  if (closeTimer) { clearTimeout(closeTimer); closeTimer = null }
  visible.value = true
  // Start invisible at the resting spot so we never flash the full-size frame
  // for a frame before the FLIP transform is seeded.
  frameStyle.value = { transition: 'none', opacity: '0' }

  await nextTick()
  const frame = frameRef.value
  if (!frame) { frameStyle.value = {}; return }

  const seed = prefersReducedMotion() ? null : flipTransform(frame)
  if (!seed) {
    // Reduced motion or no source geometry: just show it at rest.
    frameStyle.value = {}
    return
  }

  // Seed at the source position/size, then release to the resting identity.
  frameStyle.value = { transition: 'none', opacity: '1', ...seed }
  await nextFrame()
  frameStyle.value = {
    transition: `transform ${DURATION}ms cubic-bezier(0.22, 1, 0.36, 1)`,
    transformOrigin: 'top left',
    transform: 'none',
    opacity: '1',
  }
}

const closeZoom = (): void => {
  const frame = frameRef.value
  if (!frame || prefersReducedMotion()) {
    finishClose()
    return
  }

  if (!sourceInViewport()) {
    // Source scrolled away — fade out where it floats rather than fly off.
    frameStyle.value = { transition: `opacity ${DURATION}ms ease`, opacity: '0' }
    closeTimer = setTimeout(finishClose, DURATION)
    return
  }

  const back = flipTransform(frame)
  if (!back) { finishClose(); return }
  frameStyle.value = { transition: `transform ${DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`, ...back }
  closeTimer = setTimeout(finishClose, DURATION)
}

const finishClose = (): void => {
  if (closeTimer) { clearTimeout(closeTimer); closeTimer = null }
  visible.value = false
  frameStyle.value = {}
  emit('closed')
}

const requestClose = (): void => emit('close')

// Capture-phase so we intercept the click before any message/button handler —
// that swallow is what makes prose buttons inert (and panic-clicks safe) while
// the peek is open. Composer + chrome are excluded so they stay usable.
const onDocumentClickCapture = (event: MouseEvent): void => {
  const target = event.target as HTMLElement | null
  if (target?.closest?.(KEEP_ALIVE_SELECTOR)) return
  event.preventDefault()
  event.stopPropagation()
  requestClose()
}

const onKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'Escape') requestClose()
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      document.addEventListener('click', onDocumentClickCapture, true)
      window.addEventListener('keydown', onKeydown)
      void openZoom()
    } else {
      document.removeEventListener('click', onDocumentClickCapture, true)
      window.removeEventListener('keydown', onKeydown)
      if (visible.value) closeZoom()
    }
  },
)

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClickCapture, true)
  window.removeEventListener('keydown', onKeydown)
  if (closeTimer) clearTimeout(closeTimer)
})
</script>

<style scoped>
/* Non-modal peek: pinned to the top of the reading area (below the recessive
   chrome, above the composer band via the sizing calc). `pointer-events: none`
   lets the live message list scroll and read underneath it. Centered with
   margin-auto so `transform` stays free for the FLIP animation. */
.portrait-zoom__frame {
  position: fixed;
  top: calc(var(--chrome-height, 56px) + 16px);
  left: 0;
  right: 0;
  margin-inline: auto;
  z-index: 60;
  /* Square so the FLIP scale stays uniform (no mid-flight distortion) and the
     enlarged portrait is a faithful, bigger crop of the same avatar framing. */
  --zoom-size: min(
    86vw,
    calc(100dvh - var(--chrome-height, 56px) - var(--composer-band-height, 96px) - 48px)
  );
  width: var(--zoom-size);
  height: var(--zoom-size);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.55);
  pointer-events: none;
  will-change: transform;
}

.portrait-zoom__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media (min-width: 769px) {
  .portrait-zoom__frame {
    --zoom-size: min(
      440px,
      60vh,
      calc(100dvh - var(--chrome-height, 56px) - var(--composer-band-height, 96px) - 48px)
    );
  }
}

@media (prefers-reduced-motion: reduce) {
  .portrait-zoom__frame {
    will-change: auto;
  }
}
</style>
